import { searchParamsToObject } from "@helpers";
import { formatNumberText } from "@helpers";
import { useExplore } from "@hooks";
import clsx from "clsx";
import {
  arrayOf,
  bool,
  element,
  func,
  object,
  oneOfType,
  string,
} from "prop-types";
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  FormControl,
  InputGroup,
  Dropdown,
  Row,
  Stack,
  Spinner,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { defaultFilters, pageSizeOptions, sortOptions } from "../Data";
import styles from "../styles/Explorer.module.css";

const Explorer = ({
  toggleFilterUI,
  ExtendResultSummary,
  ExtendSortOptions,
}) => {
  // state
  const [keyword, setKeyword] = useState("");
  const [orderby, setOrderby] = useState(sortOptions[0]);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [totalAppliedFilters, setTotalAppliedFilters] = useState(0);

  // variables
  let start = 0;
  let end = 0;
  let resultSummary = "";

  // logics before hooks

  // hooks
  const [searchParams] = useSearchParams();
  const { exploreCourse } = useSelector((state) => state.course);
  const { search, sort, changePageSize } = useExplore();

  // logics
  const handleKeywordChange = (event) => {
    setKeyword(event.target.value);
    search(event.target.value);
  };

  const handleOrderChange = (option) => {
    setOrderby(option);
    sort(option.value);
  };

  const handlePageSizeChange = (option) => {
    setPageSize(option);
    changePageSize(option.value);
  };

  // UI partials
  if (exploreCourse.pagination) {
    const { CurrentPage, PageSize, TotalCount } = exploreCourse.pagination;
    start = (CurrentPage - 1) * PageSize + 1;
    end = CurrentPage * PageSize;
    resultSummary = TotalCount
      ? `Showing ${start} to ${
          end < TotalCount ? end : TotalCount
        } of total ${formatNumberText(TotalCount, "result")}`
      : "No result";
  }

  const searchBox = (
    <InputGroup>
      <InputGroup.Text className={styles.searchBoxPrefix}>
        <i className="bi bi-search" />
      </InputGroup.Text>
      <FormControl
        type="search"
        placeholder="Search"
        value={keyword}
        onChange={handleKeywordChange}
      />
    </InputGroup>
  );

  const filterBox = (
    <Row className="align-items-center g-1">
      <Col xs={"auto"}>
        <Button
          variant="light"
          className="border"
          onClick={toggleFilterUI}
          disabled={exploreCourse.processing}
        >
          <i className="bi bi-funnel" />
          <span className="ml-2">
            {formatNumberText(totalAppliedFilters, "Filter")}
          </span>
        </Button>
      </Col>
      <Col xs={"auto"}>
        <Dropdown>
          <Dropdown.Toggle
            id="sortPopover"
            variant="light"
            className="border"
            disabled={exploreCourse.processing}
          >
            <i className="bi bi-bar-chart" />
            <span className="mx-2">
              Sort <strong>{orderby.label}</strong>
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu variant="light" className="w-100">
            {(ExtendSortOptions || sortOptions).map((option) => (
              <Dropdown.Item
                className={clsx(
                  styles.option,
                  orderby.value === option.value ? styles.active : ""
                )}
                key={option.id}
                active={orderby.value === option.value}
                onClick={() => handleOrderChange(option)}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Col>
      <Col xs={"auto"}>
        <Dropdown>
          <Dropdown.Toggle
            id="sortPopover"
            variant="light"
            className="border"
            disabled={exploreCourse.processing}
          >
            Show <strong>{pageSize.label}</strong>
          </Dropdown.Toggle>
          <Dropdown.Menu variant="light" className="w-100">
            {pageSizeOptions.map((option) => (
              <Dropdown.Item
                className={clsx(
                  styles.option,
                  pageSize.value === option.value ? styles.active : ""
                )}
                key={option.id}
                active={pageSize.value === option.value}
                onClick={() => handlePageSizeChange(option)}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Col>
      <Col xs={"auto"}>
        <Button variant="light" disabled>
          {ExtendResultSummary ? (
            ExtendResultSummary
          ) : exploreCourse.processing ? (
            <>
              <Spinner animation="border" size="sm" variant="danger" />
              <span className="ml-2">Searching...</span>
            </>
          ) : (
            <strong>{resultSummary}</strong>
          )}
        </Button>
      </Col>
    </Row>
  );

  // effects
  useEffect(() => {
    const Keyword = searchParams.get("Keyword") || "";
    const orderby = sortOptions.find(
      (one) =>
        one.value === (searchParams.get("Orderby") || sortOptions[0].value)
    );
    const pageSize = pageSizeOptions.find(
      (one) =>
        one.value ===
        (Number(searchParams.get("PageSize")) || pageSizeOptions[0].value)
    );
    setKeyword(Keyword);
    setOrderby(orderby);
    setPageSize(pageSize);
  }, []);

  useEffect(() => {
    const countAppliedFilters = searchParamsToObject(
      searchParams,
      defaultFilters
    )[1];
    setTotalAppliedFilters(countAppliedFilters);
  }, [searchParams]);

  // render
  return (
    <Stack gap={4} className="my-4">
      {searchBox}
      {filterBox}
    </Stack>
  );
};

/* required: display name */
Explorer.displayName = "Explorer";

/* required: props types */
Explorer.propTypes = {
  toggleFilterUI: func,
  ExtendResultSummary: oneOfType([string, bool, element]),
  ExtendSortOptions: oneOfType([bool, arrayOf(object)]),
};

/* required: default props */
Explorer.defaultProps = {
  toggleFilterUI: () => {},
  ExtendResultSummary: false,
  ExtendSortOptions: false,
};

export { Explorer };
